import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../../api/store";
import { v4 as uuidv4 } from "uuid";
import { SINGLE_RIDER_IDS_COVERAGE, SINGLE_RIDER_IDS_DISEASE, SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO, SINGLE_RIDER_IDS_GCI_TOP4, SINGLE_RIDER_IDS_GCIR } from "../../../../constant/gms-constant";
import { CoverageBySalaryRankState, ProposalSalaryRanking, RiderSalaryRankInputPayload, RowData, SalaryRankRider } from "./coverage-type-by-salary-rank.state";

const createRow = (): RowData => ({
    id: uuidv4(),
    multiplierLabel: "X12",
    multiplierValue: 12,
    salary: 0,
    designation: "",
    result: 0,
    checked: false,
});

export const initialRows: RowData[] = [createRow(), createRow()];

const initialState: CoverageBySalaryRankState = {
    rows: initialRows,
    riders: [],
};

const salaryRankSlice = createSlice({
    name: "salaryRankSlice",
    initialState,
    reducers: {
        resetSalaryRankType: () => ({ ...initialState }),
        updateSalaryRankRow: (state, action: PayloadAction<{ id: string; field: keyof RowData; value: string}>) => {
            const { id, field, value } = action.payload;

            const row = state.rows.find(r => r.id === id);
            if (!row) return;

            // UPDATE MAIN ROWS
            if (field === "salary") {
                const salary = Number(value) || 0;
                row.salary = salary;
                row.result = salary * row.multiplierValue;
            }

            else if (field === "multiplierValue") {
                let multiplier = Number(value);

                if (isNaN(multiplier)) return;

                if (multiplier < 12) multiplier = 12;
                if (multiplier > 48) multiplier = 48;

                row.multiplierValue = multiplier;
                row.multiplierLabel = `X${multiplier}`;
                row.result = row.salary * multiplier;
            }

            else if (field === "multiplierLabel") {
                const raw = String(value).replace(/\D/g, "");

                // handle empty properly
                if (raw === "") {
                    row.multiplierValue = 0;
                    row.multiplierLabel = "";
                    row.result = 0;
                    return;
                }

                const multiplier = Number(raw);

                row.multiplierValue = multiplier;
                row.multiplierLabel = `X${multiplier}`;
                row.result = row.salary * multiplier;
            }

            else if (field === "designation") {
                row.designation = String(value);
            }

            else {
                (row as any)[field] = value;
            }

            // 🔥 SYNC TO RIDERS
            state.riders.forEach(rider => {
                const rRow = rider.rider.find(r => r.id === id);
                if (!rRow) return;

                // mirror same row changes
                rRow.salary = row.salary;
                rRow.multiplierValue = row.multiplierValue;
                rRow.multiplierLabel = row.multiplierLabel;
                rRow.designation = row.designation;
                rRow.result = row.result;
            });
        },
        updateMultiplierValue: ( state, action: PayloadAction<{ id: string; value: string }> ) => {
            const { id, value } = action.payload;
            const row = state.rows.find(r => r.id === id);
            if (!row) return;

            const raw = value.replace(/\D/g, "");

            // allow empty while typing
            if (raw === "") {
                row.multiplierValue = 0;
                row.multiplierLabel = "";
                row.result = 0;
                return;
            }

            const multiplier = Number(value);

            if (isNaN(multiplier)) return;

            // NO clamping here (important for UX)
            row.multiplierValue = multiplier;
            row.multiplierLabel = `X${multiplier}`;
            row.result = row.salary * multiplier;
        },
        addRiderSalaryRankInput: (state, action: PayloadAction<RiderSalaryRankInputPayload>) => {
            const { id, label, values = [], riderValues = [], type = 'checkbox'} = action.payload;
            
            const singleSelectGroup: number[] = SINGLE_RIDER_IDS_COVERAGE;
            const singleSelectGCIR: number[] = SINGLE_RIDER_IDS_GCIR;
            const singleSelectDisease: number[] = SINGLE_RIDER_IDS_DISEASE;

            const singleSelectGCI_TOP4: number[] = SINGLE_RIDER_IDS_GCI_TOP4;
            const singleSelectGCI_DENGUE_MOSQUITO: number[] = SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO;

            if (singleSelectGroup.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGroup.includes(r.id)
                ) || [];
            }

            if (singleSelectGCIR.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGCIR.includes(r.id)
                ) || [];
            }

            if (singleSelectDisease.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectDisease.includes(r.id)
                ) || [];
            }
            // GCI
            if (singleSelectGCI_TOP4.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGCI_TOP4.includes(r.id)
                ) || [];
            }
            if (singleSelectGCI_DENGUE_MOSQUITO.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGCI_DENGUE_MOSQUITO.includes(r.id)
                ) || [];
            }

            const exists = state.riders.some(r => r.id === id);
            if (exists) return;

            let finalRows: RowData[] = [];
            const hasApiValues = values.length > 0;
            const hasCheckboxValues = riderValues.length > 0;

            switch (type) {
                case "api":
                    finalRows = values.map(v => {
                        const baseRow = state.rows.find(r => r.designation === v.designation);

                        return {
                            id: baseRow?.id ?? uuidv4(),
                            multiplierLabel: baseRow?.multiplierLabel ?? "",
                            multiplierValue: baseRow?.multiplierValue ?? 0,
                            salary: v.amount ?? 0,
                            designation: v.designation,
                            result: v.amount,
                            checked: false,
                        };
                    });
                    break;

                case "checkbox":
                    finalRows = riderValues.map(v => ({
                        id: v.id ?? uuidv4(),
                        multiplierLabel: v.multiplierLabel,
                        multiplierValue: v.multiplierValue,
                        salary: v.salary,
                        designation: v.designation,
                        result: v.result,
                        checked: false,
                    }));
                    break;

                default:
                    finalRows = state.rows.map(row => ({
                        ...row,
                        id: row.id ?? uuidv4(),
                    }));
                break;
            }

            state.riders.push({
                id,
                label,
                rider: finalRows
            });
        },
        removeRiderSalaryRankInput: (state, action: PayloadAction<number>) => {
            state.riders = state.riders.filter(r => r.id !== action.payload);
        },
        updateSalaryRankRiderResult: ( state, action: PayloadAction<{ riderId: number; id: string; result: number }> ) => {
            const { riderId, id, result } = action.payload;

            const rider = state.riders.find(r => r.id === riderId);
            if (!rider) return;

            const row = rider.rider.find(r => r.id === id);
            if (!row) return;

            row.result = result;
        },
        resetSalaryRank: (state) => {
            state.rows = initialRows.map(row => ({ ...row }));
            state.riders = [];
        },
        setSalaryRankFromAPI: ( state, action: PayloadAction<ProposalSalaryRanking[]> ) => {
            
            const apiData = action.payload;
            state.rows = [];
            
            apiData.forEach((item) => {
                const multiplier = Number(item.salary_multiplier.replace("X", ""));
                
                state.rows.push({
                    id: uuidv4(),
                    multiplierValue: multiplier,
                    multiplierLabel: `X${multiplier}`,
                    salary: item.amount,
                    designation: item.designation,
                    result: item.amount * multiplier,
                });
            });
        },
        addMultiplierRow: (state) => {
            const hasEmptyRow = state.rows.some(row =>
                row.multiplierValue === 0 ||
                row.multiplierValue === 0 ||
                row.salary === 0 ||
                row.designation === ""
            );
            if (hasEmptyRow) {
                return;
            }
            const newRow: RowData = {
                id: uuidv4(),
                multiplierLabel: "12",
                multiplierValue: 12,
                salary: 0,
                designation: "",
                result: 0,
                checked: false,
            };

            state.rows.push(newRow);

            state.riders.forEach(rider => {
                rider.rider.push({ ...newRow });
            });
        },
        removeMultiplierRow: (state, action: PayloadAction<string>) => {
            const id = action.payload;

            if (state.rows.length <= 2) return;
            state.rows = state.rows.filter(row => row.id !== id);

            state.riders.forEach(rider => {
                rider.rider = rider.rider.filter(row => row.id !== id);
            });
        },
        updateSalaryRankRider: (state, action: PayloadAction<SalaryRankRider[]> ) => {
            const updatedRiders = action.payload;

            updatedRiders.forEach(updatedRider => {
                const index = state.riders.findIndex(r => r.id === updatedRider.id);
                if (index === -1) return;

                state.riders[index] = {
                    ...updatedRider,
                    rider: updatedRider.rider.map(row => ({
                        ...row,
                        id: row.id ?? uuidv4(),
                    })),
                };
            });
        },
    },
});

export const {
    resetSalaryRankType,
    updateSalaryRankRow,
    updateMultiplierValue,
    addRiderSalaryRankInput,
    removeRiderSalaryRankInput,
    resetSalaryRank,
    updateSalaryRankRiderResult,
    setSalaryRankFromAPI,
    addMultiplierRow,
    removeMultiplierRow,
    updateSalaryRankRider
} = salaryRankSlice.actions;

export const selectSalaryRank = (state: RootState) => state.salaryRankSlice.rows;
export const selectCheckedSalaryRank = createSelector([selectSalaryRank], (rows) => rows.filter(row => row.checked));
export const selectSalaryRankRiders = (state: RootState) => state.salaryRankSlice.riders;

export default salaryRankSlice.reducer;